import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Collapse,
	Divider,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { formatAge } from '@/utils/ageHelper';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useDeleteAppendix6Mutation,
	useGetAppendix6DetailQuery,
} from '@/api/forms-management/appendix6';
import LogHistory from '../LogHistory';
import FormManagementChip from '@/common/components/FormManagementChip';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
	useGetAppendix6GenerateReportMutation,
	useGetExportCustomerPDFMutation,
} from '@/api/common';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function Appendix6DetailPage() {
	const { t } = useTranslation();
	const { preAdmissionId, appendix6Id, admissionRequestId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [deleteAppendix6] = useDeleteAppendix6Mutation();
	const showModal = useModal();

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getAppendix6GenerateReport] = useGetAppendix6GenerateReportMutation();

	const [fromInformationExpanded, setFromInformationExpanded] = useState(true);
	const [toInformationExpanded, setToInformationExpanded] = useState(true);
	const [referralInformationExpanded, setReferralInformationExpanded] =
		useState(true);

	const { data: details }: any = useGetAppendix6DetailQuery(appendix6Id || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('preAdmission.appendix6ServiceAdmission'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteAppendix6(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							preAdmissionId
								? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
								: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`,
							{ replace: true }
						);
					}
				}}
			/>
		));
	};

	const formartServiceRendered = () => {
		const arr = [];

		if (details?.data.counsellingToServiceUser)
			arr.push(t('preAdmission.mentalSuspectedMentalProblems'));
		if (details?.data.counsellingToFamily)
			arr.push(t('preAdmission.memberCarerServiceUser'));
		if (details?.data.successfulEngagement)
			arr.push(
				t('preAdmission.supportDevelopmentalGroupsEducationalProgrammes')
			);
		if (details?.data.occupationalTherapy)
			arr.push(
				t('preAdmission.serviceUserReceiving') +
					' ' +
					t('preAdmission.occupationalTherapy')
			);
		if (details?.data.outreaching)
			arr.push(
				t('preAdmission.serviceUserReceiving') +
					' ' +
					t('preAdmission.outreaching')
			);
		if (details?.data.trainingServices)
			arr.push(
				t('preAdmission.serviceUserReceiving') +
					' ' +
					t('preAdmission.trainingServices')
			);
		if (details?.data.successfullyReferred)
			arr.push(t('preAdmission.successfullyReferred'));
		if (details?.data.peerSupportService)
			arr.push(t('preAdmission.peerSupportService'));
		if (details?.data.clinicalPsychologicalService)
			arr.push(t('preAdmission.clinicalPsychologicalService'));
		if (details?.data.othersSpecify) arr.push(t('preAdmission.othersSpecify'));

		return arr.join(';\n');
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.appendix6Detail')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response = await getAppendix6GenerateReport(
									details?.data.id
								);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
						<Button
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => handleDelete()}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setFromInformationExpanded(!fromInformationExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.from')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: fromInformationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={fromInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.officerInCharge')}
									value={details?.data.fromOfficerInCharge}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.unit')}
									value={details?.data.branchName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.ref')}
									value={details?.data.fromRefNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNo')}
									value={details?.data.fromTelNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.faxNo')}
									value={details?.data.fromFaxNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.date')}
									value={
										details?.data.fromDate
											? dayjs(details?.data.fromDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setToInformationExpanded(!toInformationExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.to')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: toInformationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={toInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.officerInCharge')}
									value={details?.data.toOfficerInCharge}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.nameOfTheOrganisation')}
									value={details?.data.nameOfOrganization}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.ref')}
									value={details?.data.toRefNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dated')}
									value={
										details?.data.toDated
											? dayjs(details?.data.toDated)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.faxNo')}
									value={details?.data.toFaxNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.totalPages')}
									value={details?.data.toTotalPages}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setReferralInformationExpanded(!referralInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.receiptOfReferralForICCMW')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: referralInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={referralInformationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name')}
									value={details?.data.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.hkicNo')}
									value={details?.data.hkicNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.sex')}
									value={details?.data.sexDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.age')}
									value={formatAge(
										details?.data.age,
										details?.data.ageEstimated
									)}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t(
										'preAdmission.referringAboveNamedServiceUserUouReferredOn'
									)}
									value={
										details?.data.referralDate
											? dayjs(details?.data.referralDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.serviceRendered')}
									value={formartServiceRendered()}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.remarks')}
									value={details?.data.remarks}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ margin: '24px 0' }} />
						<Box display='flex' justifyContent='space-between' marginBottom={2}>
							<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
								{t('preAdmission.shouldEnquiries')}
							</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name')}
									value={details?.data.contactWorkerName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.telNo')}
									value={details?.data.contactTel}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.officerInCharge')}
									value={details?.data.contactOfficerInCharge}
								/>
							</Grid>
						</Grid>
					</Collapse>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
